
/* eslint-disable react/display-name */
/* eslint-disable max-len */
import { format, getMonth, getYear } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import styled, { css } from 'styled-components';
import { PopperInner, PopperMount } from '../app/canvas/ui/PopperElements';
import { Flexer } from '../app/dashboard/elements/LayoutElements';
import { Icon } from '../concepts/resources/Icon';
import { Colors } from './Colors';
import { DropButton } from './DropButton';
import { Float } from './Float';
import { Grid } from './Grid';
import { usePrompt } from './Prompt';

const Frame = styled.div`
    padding: .5rem;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: .5rem;
`;

const Year = styled.div`
    font-size: 1.125rem;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
`;

const NavButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 100%;
    cursor: pointer;
    user-select: none;
    
    &:hover {
        background: ${Colors.hover};
    }
    &:active {
        background: ${Colors.active};
    }
`;

const MonthButton = styled.div<{ disabled?: boolean, highlight?: 'start' | 'middle' | 'end', selected?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 4rem;
    height: 2.5rem;
    margin: .125rem 0;
    border-radius: 0.25rem;
    cursor: pointer;
    user-select: none;
    
    ${p => p.highlight && css`
        background: ${Colors.hover};
        ${p.highlight === 'start' && css` border-radius: 0.25rem 0 0 0.25rem; `}
        ${p.highlight === 'end' && css` border-radius: 0 0.25rem 0.25rem 0; `}
        ${p.highlight === 'middle' && css` border-radius: 0; `}
    `}

    &:hover {
        background: ${Colors.hover};
    }

    &:active {
        background: ${Colors.active};
    }

    ${p => p.selected && css`
        background: ${Colors.primary} !important;
        color: white;
    `}

    ${p => p.disabled && css`
        pointer-events: none;
        filter: grayscale(50%);
        opacity: .33;
    `}
`;

const months = Array.from({ length: 12 }).map((_, i) => format(new Date(1970, i, 1), 'MMM'));

class YearMonth {

    public static for(date: Date) {
        return new YearMonth(getYear(date), getMonth(date));
    }

    constructor(public y: number, public m: number) {
    }

    public get id() {
        return `${this.y}-${this.m}`;
    }

    public label(mode: 'm' | 'y' | 'my') {
        if (mode === 'm') return `${format(this.toDate(), 'MMM')}`;
        if (mode === 'y') return `${this.y}`;
        return `${months[this.m]} ${this.y}`;
    };

    public equals(other: YearMonth) {
        return this.m === other.m && this.y === other.y;
    }

    public lessThan(other: YearMonth) {
        return this.y < other.y || (this.y === other.y && this.m < other.m);
    }

    public greaterThan(other: YearMonth) {
        return this.y > other.y || (this.y === other.y && this.m > other.m);
    }

    public difference(other: YearMonth) {
        return (this.y - other.y) * 12 + (this.m - other.m);
    }

    public toDate() {
        return new Date(this.y, this.m, 1);
    }
}

type SinglePickerProps = {
    mode: 'single',
    date: Date,
    notBefore?: Date,
    notAfter?: Date,
    onChange: (date: Date) => void,
};

type RangePickerProps = {
    mode: 'range',
    range?: readonly [Date, Date],
    notBefore?: Date,
    notAfter?: Date,
    onChange: (range: readonly [Date, Date]) => void,
};

export type MonthPickerProps = SinglePickerProps | RangePickerProps;

const isMonthDisabled = (at: YearMonth, notBefore?: Date, notAfter?: Date) => {
    if (notBefore && at.lessThan(YearMonth.for(notBefore))) {
        return true;
    }
    if (notAfter && at.greaterThan(YearMonth.for(notAfter))) {
        return true;
    }
    return false;
};

const getMonthHighlight = (at: YearMonth, from: YearMonth, to: YearMonth): 'start' | 'middle' | 'end' | null => {
    if (at.equals(from) && at.equals(to)) return null;
    if (at.equals(from)) return 'start';
    if (at.equals(to)) return 'end';
    if (at.greaterThan(from) && at.lessThan(to)) return 'middle';
    return null;
};

const SinglePicker = React.memo<SinglePickerProps>(({ date, notBefore, notAfter, onChange }) => {

    const value = useMemo(() => YearMonth.for(date), [date]);

    const [viewYear, setViewYear] = useState(value.y);
    const viewMonths = useMemo(() => Array.from({ length: 12 }).map((_, i) => new YearMonth(viewYear, i)), [viewYear]);

    const onClickButton = useCallback((x: YearMonth) => onChange(x.toDate()), [onChange]);

    return <Frame>
        <Header>
            <NavButton onClick={() => setViewYear(x => x - 1)}>
                <Icon size="1.5" src="deqo://icons/hero/chevron-left" />
            </NavButton>
            <Year>{viewYear}</Year>
            <NavButton onClick={() => setViewYear(x => x + 1)}>
                <Icon size="1.5" src="deqo://icons/hero/chevron-right" />
            </NavButton>
        </Header>
        <Grid columns="1fr 1fr 1fr">
            {viewMonths.map((x, i) =>
                <MonthButton
                    key={x.id}
                    disabled={isMonthDisabled(x, notBefore, notAfter)}
                    selected={x.equals(value)}
                    onClick={() => onClickButton(x)}>
                    {x.label('m')}
                </MonthButton>
            )}
        </Grid>
    </Frame>;
});

const RangePicker = React.memo<RangePickerProps>(({ range, notBefore, notAfter, onChange }) => {

    const [from, to] = useMemo(() => {
        const [f, t] = range ?? [new Date(), new Date()];
        return [YearMonth.for(f), YearMonth.for(t)];
    }, [range]);

    const [viewYear, setViewYear] = useState(to.y);
    const viewMonths = useMemo(() => Array.from({ length: 12 }).map((_, i) => new YearMonth(viewYear, i)), [viewYear]);

    const onClickButton = useCallback((x: YearMonth) => {
        // If click on the to date, reset range to that value
        if (x.equals(to)) {
            onChange([x.toDate(), x.toDate()]);
        }
        // If click is before from, extend from
        else if (x.lessThan(from)) {
            onChange([x.toDate(), to.toDate()]);
        }
        // Otherwise, assume new to value
        else {
            onChange([from.toDate(), x.toDate()]);
        }
    }, [viewYear, from, to, onChange]);

    return <Frame>
        <Header>
            <NavButton onClick={() => setViewYear(x => x - 1)}>
                <Icon size="1.5" src="deqo://icons/hero/chevron-left" />
            </NavButton>
            <Year>{viewYear}</Year>
            <NavButton onClick={() => setViewYear(x => x + 1)}>
                <Icon size="1.5" src="deqo://icons/hero/chevron-right" />
            </NavButton>
        </Header>
        <Grid columns="1fr 1fr 1fr">
            {viewMonths.map((x, i) =>
                <MonthButton
                    key={x.id}
                    disabled={isMonthDisabled(x, notBefore, notAfter)}
                    highlight={getMonthHighlight(x, from, to)}
                    selected={x.equals(from) || x.equals(to)}
                    onClick={() => onClickButton(x)}>
                    {x.label('m')}
                </MonthButton>
            )}
        </Grid>
    </Frame>;
});

const Base = React.memo<MonthPickerProps>((props) => {
    if (props.mode === 'single') return <SinglePicker {...props} />;
    if (props.mode === 'range') return <RangePicker {...props} />;
    return null;
});

export const MonthPicker = Object.assign(Base, { Frame, NavButton, MonthButton, Year });

export const MonthPopper = React.memo<MonthPickerProps>((props) => {

    const [open, setOpen] = useState(false);

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, { placement: 'bottom-start' });

    const promptRef = usePrompt({
        ignoreEnter: true,
        onAction: () => setOpen(false),
    });

    const onToggle = useCallback(() => setOpen(!open), [open, setOpen]);

    return <Flexer ref={promptRef} flex="row">
        <Flexer ref={setRefElmt}>
            <DropButton active={open} onClick={onToggle}>
                <span>Date</span>
            </DropButton>
        </Flexer>
        {open && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <PopperInner direction="down" offset=".5">
                <Float>
                    <MonthPicker {...props} />
                </Float>
            </PopperInner>
        </PopperMount>
        }
    </Flexer >;
});
